import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import LazyYouTubeEmbed from '@/commons/LazyYouTubeEmbed'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;

    const wpPosts = getWpHvacServices().length > 0 ? getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        countryName,
        currencyCd,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const requestType = 'Aircon Services'
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Affordable Aircon Drain Pipe Cleaning & Unclogging</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Best aircon drainage pipe chemical flushing to clear dust, dirt, and debris from clogged drainage pipe. Affordable aircon drainage pipe replacement in Singapore."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Affordable Aircon Drain Pipe Cleaning & Unclogging`}/>
        <meta property="og:description" content="Best aircon drainage pipe chemical flushing to clear dust, dirt, and debris from choked drainage pipe. Affordable aircon drainage pipe replacement in Singapore." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-18T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-drainage-pipe-blockage-diagram.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-drainage-pipe-blockage-diagram.webp" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe"/>
        <meta name="twitter:title" content={`Affordable Aircon Drain Pipe Cleaning & Unclogging`}/> 
        <meta name="twitter:description" content="Best aircon drainage pipe chemical flushing to clear dust, dirt, and debris from choked drainage pipe. Affordable aircon drainage pipe replacement in Singapore."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe"},"headline":"Affordable Aircon Drain Pipe Cleaning & Unclogging","description":"Best aircon drainage pipe chemical flushing to clear dust, dirt, and debris from choked drainage pipe. Affordable aircon drainage pipe replacement in Singapore.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-drainage-pipe-blockage-diagram.webp"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-18T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe","name":"Aircon Drainage Pipe","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-drainage-pipe"}}]}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-drain-pipe-cleaning" className='relative'>
                    <AirconRepair 
                        setRequestForm={setRequestForm} 
                        setIsQuickBook={setIsQuickBook} 
                        title={"Affordable Aircon Drain Pipe Cleaning & Unclogging"} 
                        problem={"Aircon Drain Pipe is Clogged"}
                        heroImage="aircon/2026/aircon-drainage-pipe-blockage-diagram.webp"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='max-w-4xl mx-auto py-10 px-2 leading-8'>
                <div className='py-2 space-y-5'>
                    <div className='space-y-2'>
                        <h2 className='font-bold text-xl uppercase text-gray-400'>What is aircon drainage pipe?</h2>
                        <p className=''>
                            An aircon drainage pipe, also known as an air conditioner drainage pipe or condensate drain pipe, is a 
                            component of an air conditioning system that is responsible for removing the condensation that forms during 
                            the cooling process.
                        </p>
                        <h2 className='font-bold text-xl uppercase pt-4 text-gray-400'>How does the drainage pipe work?</h2>
                        <p>
                            When an air conditioner operates, it cools the air by removing heat and moisture from the indoor 
                            environment. As a result, water vapor in the air condenses into liquid form, creating moisture. 
                            This moisture needs to be drained away from the air conditioning unit to prevent it from causing 
                            damage or excessive humidity in the surrounding area.
                        </p>
                        <h2 className='font-bold text-xl uppercase pt-4 text-gray-400'>Where and how is the drain pipe installed?</h2>
                        <p>
                            The aircon drainage pipe is typically a plastic or PVC pipe that is connected to the indoor unit of the 
                            air conditioner. It collects the condensed water and directs it to a suitable drainage point, such as a 
                            floor drain or an outdoor area where the water can safely drain away. The pipe is designed with a slight 
                            downward slope to ensure proper drainage and prevent water from accumulating or causing leaks.
                        </p>
                    </div> 
                    <div className='space-y-2'>
                        <h2 className='font-bold text-2xl'>Cleaning & Maintenance of Drainage Pipe</h2>
                        <p>
                        Aircon drainage pipe cleaning is necessary for several reasons:
                        </p>
                        <div className='space-y-2 pt-2'>
                            <h3 className='font-bold'>1. Preventing blockages</h3>
                            <p>
                            Over time, dust, dirt, and debris can accumulate inside the aircon drainage pipe. If left uncleaned, 
                            these substances can cause blockages, hindering the flow of water. A clogged drainage pipe can lead to 
                            water leakage from the air conditioner, potentially causing damage to the unit or the surrounding area.
                            </p>
                        </div>
                        <div className='space-y-2 pt-2'>
                            <h3 className='font-bold'>2. Avoiding water damage</h3>
                            <p>
                            When the aircon drainage pipe is clogged, the condensed water may overflow from the drain pan or drip tray. 
                            This can result in water damage to the walls, floors, or furniture near the air conditioning unit. Regular 
                            cleaning helps prevent these issues by ensuring that the water drains properly.
                            </p>
                        </div>
                        <div className='space-y-2 pt-2'>
                            <h3 className='font-bold'>3. Reducing the risk of mold and bacteria growth</h3>
                            <p>
                                Moisture and organic matter inside the aircon drainage pipe create a conducive environment for the 
                                growth of mold, algae, and bacteria. If left untreated, these microorganisms can spread throughout 
                                the air conditioning system and into the indoor air, potentially causing health issues for occupants. 
                                Cleaning the drainage pipe helps inhibit the growth of these harmful organisms and improves indoor air 
                                quality.
                            </p>
                        </div>     
                        <div className='space-y-2 pt-2'>
                            <h3 className='font-bold'>4. Maintaining optimal system performance</h3>
                            <p>
                            A clean and unobstructed aircon drainage pipe allows for efficient water drainage from the air conditioning 
                            unit. When the drainage system functions properly, the unit can operate at its optimal performance, 
                            cooling the air effectively and efficiently. This helps maintain a comfortable indoor environment while 
                            reducing energy consumption.
                            </p>
                        </div>
                        <p>
                        To ensure the aircon drainage pipe functions properly and to prevent potential issues, it is advisable to 
                        include regular maintenance, such as cleaning and inspection, as part of your air conditioning system&apos;s care 
                        routine.
                        </p>
                    </div>                   
                    <div className='relative h-96'>
                        <Image className='rounded-lg'
                            src={s3Static + "blog/living/cool-air-from-aircon.jpg"}
                            fill
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon blower blowing cold air'
                        />          
                    </div>
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>Aircon Drainage Pipe Unclogging</h2>
                        <div className='space-y-2'>
                            <p className=''>
                            Aircon drain pipe unclogging is the process of clearing and 
                            cleaning the drainage pipe of an air conditioning system. This procedure is performed to remove any 
                            blockages, debris, algae, or mold growth that may have accumulated within the drain pipe, preventing 
                            proper water drainage.
                            <br/>
                            Here is a general overview of the process involved in aircon drain pipe flushing:
                            </p>
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>1. Preparation</h3>
                                <p>
                                Ensure that the air conditioning system is turned off and unplugged to prevent any electrical hazards. Gather the necessary tools 
                                and materials, such as a wet/dry vacuum, a cleaning brush or wire, a clean cloth, and a mild cleaning solution (optional).
                                </p>
                            </div>
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>2. Accessing the drain pipe</h3>
                                <p>
                                Locate the aircon drain pipe, which is usually connected to the indoor unit of the air conditioner. The drain pipe can typically 
                                be found near the condensate drain pan or drip tray.
                                </p>
                            </div>
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>3. Removing debris</h3>
                                <p>
                                Inspect the drain pipe for any visible debris, such as dirt, dust, or leaves. Use a cleaning brush or wire to gently remove the 
                                obstructions, being careful not to damage the pipe.
                                </p>
                            </div>     
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>4. Flushing with water</h3>
                                <p>
                                Using a wet/dry vacuum or a hose with low water pressure, flush water through the drain pipe to dislodge and remove any 
                                remaining debris or blockages. Start by connecting the vacuum or hose to the end of the drain pipe or by inserting it 
                                into the drain opening. Turn on the vacuum or allow a gentle flow of water to create pressure that helps flush out the 
                                accumulated material.
                                </p>
                            </div>
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>5. Repeating the process</h3>
                                <p>
                                If necessary, repeat the flushing process multiple times until the water flows freely through the drain pipe without any obstructions. This helps ensure that all debris and blockages are thoroughly cleared.
                                </p>
                            </div> 
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>6. Optional cleaning solution</h3>
                                <p>
                                If the drain pipe has stubborn algae or mold growth, you may use a mild cleaning solution specifically 
                                designed for air conditioning systems. Follow the manufacturer&apos;s instructions and apply the solution to 
                                the inside of the drain pipe. Allow the solution to sit for a recommended duration to dissolve or kill 
                                the microorganisms. 
                                </p>
                            </div>     
                            <div className='space-y-2 pt-2'>
                                <h3 className='font-bold'>7. Drying and reassembly</h3>
                                <p>
                                Once the drain pipe is cleared and cleaned, allow it to dry completely. Wipe the exterior of the pipe 
                                with a clean cloth to remove any excess moisture. Reassemble any components that were removed during 
                                the process, ensuring they are properly secured.
                                </p>
                            </div>  
                            <p>
                            If you are unsure or uncomfortable performing the cleaning yourself, it is recommended to seek assistance 
                            from a professional air conditioning technician. They have the expertise and tools to effectively clean and 
                            maintain the drain pipe and other components of the air conditioning system.
                            </p>            
                        </div>
                    </div>
                    <h3 className='text-center font-bold text-lg text-gray-500 pt-10 px-2'>DIY: Video tutorial on how to clean ac drain pipe yourself</h3>
                    <LazyYouTubeEmbed videoId="LkHEOxpCu34" title="Clean aircon drain pipe" />
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>Aircon Drainage Pipe Replacement Cost</h2>
                        <div className='space-y-5'>
                            <p>
                            The cost of replacing an aircon drainage pipe can vary depending on various factors such as the location, 
                            complexity of the installation, type of pipe used, and the rates charged by the service provider. However, 
                            to provide a general estimate, the cost of replacing an aircon drainage pipe typically ranges from $100 to 
                            $500.
                            </p>
                        </div>
                    </div>             
                </div>
            </div>
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> 
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '128634041116395');
            fbq('track', 'PageView');`}
        </Script>
    </>
  )
}